import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  getDistributorList,
  getDistributorLimitations,
  saveDistributorLimitations,
  type DistributorLimitUpdatePayload,
} from "src/services/distributorService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

export const useQueryGetDistributorList = (
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.distributorList, language],
      queryFn: () => getDistributorList(language),
      enabled,
      retry: false,
      ...options,
    }),
    enabled,
  );
};

export const useQueryGetDistributorLimitations = (
  distributorId?: string | number | null,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(distributorId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.distributorLimitations, distributorId, language],
      queryFn: () => getDistributorLimitations(distributorId!, language),
      enabled,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useMutationSaveDistributorLimitations = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      distributorId,
      payload,
    }: {
      distributorId: string | number;
      payload: DistributorLimitUpdatePayload[];
    }) => saveDistributorLimitations(distributorId, payload, language),
    onSuccess: (_data, _variables) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.distributorLimitations,
        }),
        queryClient.invalidateQueries({ queryKey: QueryNames.customerLimits }),
      ]);
    },
  });
};
